import { useDraggable } from '@dnd-kit/core';
import {
  Box,
  Flex,
  Text,
  Badge,
  IconButton,
  useColorModeValue,
} from '@chakra-ui/react';
import { DragHandleIcon, CloseIcon } from '@chakra-ui/icons';

import type { AssignmentDriver } from 'api/quote-marketplace';

interface DraggableDriverCardProps {
  driver: AssignmentDriver;
  isDragOverlay?: boolean;
  isLearner?: boolean;
  learnerLabel?: string;
  isExcluded?: boolean;
  onRemove?: () => void;
}

const DraggableDriverCard = ({
  driver,
  isDragOverlay,
  isLearner,
  learnerLabel,
  isExcluded,
  onRemove,
}: DraggableDriverCardProps) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `driver-${driver.id}`,
    data: { driver, isExcluded },
  });

  const bgColor = useColorModeValue('white', 'navy.700');
  const excludedBgColor = useColorModeValue('red.50', 'red.900');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const excludedBorderColor = useColorModeValue('red.200', 'red.700');
  const dragHandleColor = useColorModeValue('gray.400', 'gray.500');

  const getBackgroundColor = () => {
    if (isExcluded) return excludedBgColor;
    return bgColor;
  };

  const displayName =
    `${driver.first_name} ${driver.last_name}`.trim() || `Driver ${driver.id}`;

  return (
    <Box
      ref={isDragOverlay ? undefined : setNodeRef}
      bg={getBackgroundColor()}
      borderWidth="1px"
      borderColor={isExcluded ? excludedBorderColor : borderColor}
      borderRadius="md"
      px={3}
      py={2}
      cursor="grab"
      opacity={isDragging && !isDragOverlay ? 0.5 : 1}
      shadow={isDragOverlay ? 'lg' : 'sm'}
      _hover={{
        shadow: 'md',
        borderColor: isExcluded ? 'red.400' : 'blue.300',
      }}
      transition="all 0.15s"
      {...(isDragOverlay ? {} : { ...listeners, ...attributes })}
    >
      <Flex align="center" gap={2}>
        <DragHandleIcon color={dragHandleColor} boxSize={3} />
        <Text
          fontSize="sm"
          fontWeight="500"
          noOfLines={1}
          flex={1}
          color={isExcluded ? 'red.600' : undefined}
        >
          {displayName}
        </Text>
        {isLearner && learnerLabel && !isExcluded && (
          <Badge
            colorScheme="gray"
            fontSize="10"
            variant="subtle"
            flexShrink={0}
          >
            {learnerLabel}
          </Badge>
        )}
        {onRemove && (
          <IconButton
            aria-label="Remove"
            icon={<CloseIcon boxSize={2} />}
            size="xs"
            variant="ghost"
            colorScheme="red"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            onPointerDown={(e) => e.stopPropagation()}
          />
        )}
      </Flex>
    </Box>
  );
};

export default DraggableDriverCard;
